import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { toast } from 'react-toastify'
import {
  downloadUnitQrCode,
  downloadBuildingAccessQrCode,
  exportUnitQrCodePdf,
  exportBuildingAccessQrCodePdf,
} from 'services/qrLandingService'
import { downloadBlob } from 'utils/downloadBlob'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 41, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 16px;
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  max-height: 92vh;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.04);
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Subtitle = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;
  padding: 4px;

  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const QrFrame = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
`

const QrImg = styled.img`
  width: 240px;
  height: 240px;
  display: block;
`

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 8px 10px;
  align-items: center;
`

const LinkText = styled.div`
  flex: 1;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const IconBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.15s ease;

  &:hover { background: #f3f4f6; }
`

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const ActionBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 8px;
  padding: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) { background: #f3f4f6; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const Hint = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.55;
`

const ErrorBlock = styled.div`
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  text-align: center;
  line-height: 1.5;
`

const buildLink = ({ kind, unitId, bpId }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  if (kind === 'unit') return `${origin}/u/${unitId}`
  return `${origin}/b/${bpId}/request-access`
}

const fetchSvgBlob = ({ kind, bpId, unitId }) => {
  if (kind === 'unit') {
    return downloadUnitQrCode({ bpId, unitId, format: 'svg', size: 480 })
  }
  return downloadBuildingAccessQrCode({ bpId, format: 'svg', size: 480 })
}

const QrCodePreviewModal = ({ open, onClose, kind, bpId, unitId, name }) => {
  const [svgUrl, setSvgUrl] = useState(null)
  const [svgBlob, setSvgBlob] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pngDownloading, setPngDownloading] = useState(false)
  const [pdfDownloading, setPdfDownloading] = useState(false)

  const deepLink = useMemo(() => buildLink({ kind, unitId, bpId }), [kind, unitId, bpId])
  const subtitle = kind === 'unit'
    ? 'Scanners land on this unit. Signed-in occupants go straight in; others are prompted to sign in or request access.'
    : 'Scanners land on a request-access form. Use on lobby doors / entrance signs.'

  useEffect(() => {
    if (!open) return
    let url = null
    let cancelled = false
    setLoading(true)
    setError(null)
    setSvgBlob(null)
    setSvgUrl(null)
    fetchSvgBlob({ kind, bpId, unitId })
      .then(res => {
        if (cancelled) return
        const blob = res?.data
        if (!blob) {
          setError("Couldn't load the QR code.")
          return
        }
        url = URL.createObjectURL(blob)
        setSvgUrl(url)
        setSvgBlob(blob)
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the QR code. Please try again.")
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [open, kind, bpId, unitId])

  if (!open) return null

  const safeName = (name || (kind === 'unit' ? 'unit' : 'building')).replace(/[^A-Za-z0-9_-]+/g, '_')

  const handleCopy = async () => {
    // navigator.clipboard requires a secure context (https or localhost).
    // Staging behind a plain http hostname will throw — fall back to the
    // hidden-textarea + execCommand pattern so the button works there too.
    const tryNativeClipboard = async () => {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false
      try {
        await navigator.clipboard.writeText(deepLink)
        return true
      } catch {
        return false
      }
    }
    const tryLegacyClipboard = () => {
      try {
        const ta = document.createElement('textarea')
        ta.value = deepLink
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.top = '0'
        ta.style.left = '0'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        const ok = document.execCommand && document.execCommand('copy')
        document.body.removeChild(ta)
        return !!ok
      } catch {
        return false
      }
    }
    if (await tryNativeClipboard()) {
      toast.success('Link copied')
      return
    }
    if (tryLegacyClipboard()) {
      toast.success('Link copied')
      return
    }
    toast.error("Couldn't copy the link — copy it manually from the field above")
  }

  const handleOpenInTab = () => {
    window.open(deepLink, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadSvg = () => {
    if (!svgBlob) return
    downloadBlob(svgBlob, `qr-${kind}-${safeName}.svg`)
  }

  const handleDownloadPng = async () => {
    setPngDownloading(true)
    try {
      const res = kind === 'unit'
        ? await downloadUnitQrCode({ bpId, unitId, format: 'png', size: 640 })
        : await downloadBuildingAccessQrCode({ bpId, format: 'png', size: 640 })
      downloadBlob(res?.data, `qr-${kind}-${safeName}.png`)
    } catch {
      toast.error("Couldn't download the PNG")
    } finally {
      setPngDownloading(false)
    }
  }

  const handleDownloadPdf = async () => {
    setPdfDownloading(true)
    try {
      const res = kind === 'unit'
        ? await exportUnitQrCodePdf({ bpId, unitId })
        : await exportBuildingAccessQrCodePdf({ bpId })
      downloadBlob(res?.data, `qr-${kind}-${safeName}.pdf`)
    } catch {
      toast.error("Couldn't download the PDF")
    } finally {
      setPdfDownloading(false)
    }
  }

  // Mount on body via portal so the modal isn't trapped inside a parent
  // with restrictive markup rules (e.g. a <tr> in the units table) or
  // overflow/transform stacking contexts that would clip it.
  if (typeof document === 'undefined') return null
  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <HeaderRow>
          <TitleBlock>
            <Title>{kind === 'unit' ? `QR for ${name || 'unit'}` : `Building entry QR — ${name || ''}`}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </TitleBlock>
          <CloseBtn onClick={onClose} aria-label="Close"><CloseIcon /></CloseBtn>
        </HeaderRow>

        <QrFrame>
          {loading && <CircularProgress size={28} />}
          {!loading && error && <ErrorBlock>{error}</ErrorBlock>}
          {!loading && !error && svgUrl && <QrImg src={svgUrl} alt="QR code" />}
        </QrFrame>

        <LinkRow>
          <LinkText title={deepLink}>{deepLink}</LinkText>
          <IconBtn onClick={handleCopy} title="Copy link">
            <ContentCopyIcon style={{ fontSize: 14 }} /> Copy
          </IconBtn>
        </LinkRow>

        <ActionGrid>
          <ActionBtn onClick={handleDownloadPng} disabled={pngDownloading}>
            <DownloadOutlinedIcon style={{ fontSize: 16 }} />
            {pngDownloading ? 'Saving…' : 'Download PNG'}
          </ActionBtn>
          <ActionBtn onClick={handleDownloadSvg} disabled={!svgBlob}>
            <DownloadOutlinedIcon style={{ fontSize: 16 }} />
            Download SVG
          </ActionBtn>
          <ActionBtn onClick={handleDownloadPdf} disabled={pdfDownloading}>
            <PrintOutlinedIcon style={{ fontSize: 16 }} />
            {pdfDownloading ? 'Saving…' : 'Download PDF'}
          </ActionBtn>
          <ActionBtn onClick={handleOpenInTab}>
            <OpenInNewIcon style={{ fontSize: 16 }} />
            Open in new tab
          </ActionBtn>
        </ActionGrid>

        <Hint>
          The link opens to a public landing page that shows the {kind === 'unit' ? 'unit + building name' : 'building name + address'} only. Anonymous visitors see a sign-up prompt; signed-in users with access go straight in. No data is revealed without sign-in.
        </Hint>
      </Card>
    </Overlay>,
    document.body
  )
}

export default QrCodePreviewModal
